/**
 * Signal Aggregator — aggregates intelligence signals by country
 * and detects regional convergence zones.
 *
 * 8 signal types tracked:
 * - Internet outages
 * - AIS anomalies (vessel tracking gaps)
 * - Military activity
 * - Strikes/labor actions
 * - Protests/civil unrest
 * - Fires/environmental
 * - Temporal spikes (volume anomalies)
 * - Geographic convergence
 *
 * Produces country-level signal summaries with convergence scores.
 */

import type { NormalizedEvent, Severity } from '@/sources/base-source';
import type { AnalysisResult, AggregatedSignal } from './types';
import { getEventTimestamp } from './types';

const SIX_HOURS_MS = 6 * 60 * 60 * 1000;

// ---------------------------------------------------------------------------
// Signal type classification
// ---------------------------------------------------------------------------

/** Map event types and keywords to signal categories. */
interface SignalRule {
  signalType: string;
  /** Event types that match this signal. */
  eventTypes: Set<string>;
  /** Keywords in title/tags that indicate this signal. */
  keywords: string[];
}

const SIGNAL_RULES: SignalRule[] = [
  {
    signalType: 'outage',
    eventTypes: new Set(['infrastructure', 'cyber_incident']),
    keywords: ['outage', 'blackout', 'offline', 'disruption', 'internet down'],
  },
  {
    signalType: 'ais_anomaly',
    eventTypes: new Set(['vessel_position']),
    keywords: ['dark', 'AIS gap', 'transponder off', 'went dark', 'signal lost'],
  },
  {
    signalType: 'military',
    eventTypes: new Set(['military', 'flight_position']),
    keywords: ['troops', 'deployed', 'military', 'missile', 'defense', 'naval', 'armed forces'],
  },
  {
    signalType: 'strike',
    eventTypes: new Set([]),
    keywords: ['strike', 'labor', 'walkout', 'industrial action', 'work stoppage'],
  },
  {
    signalType: 'protest',
    eventTypes: new Set(['protest', 'social']),
    keywords: ['protest', 'demonstration', 'riot', 'unrest', 'march', 'rally'],
  },
  {
    signalType: 'fire',
    eventTypes: new Set(['fire', 'disaster']),
    keywords: ['fire', 'wildfire', 'blaze', 'burn', 'explosion'],
  },
  {
    signalType: 'temporal_spike',
    eventTypes: new Set([]),
    keywords: [], // Detected from surge results, not keywords
  },
  {
    signalType: 'convergence',
    eventTypes: new Set([]),
    keywords: [], // Detected from convergence results, not keywords
  },
];

/** Classify a single event into signal types. */
function classifySignal(event: NormalizedEvent): string[] {
  const types: string[] = [];
  const text = `${event.title} ${(event.tags ?? []).join(' ')}`.toLowerCase();

  for (const rule of SIGNAL_RULES) {
    // Skip temporal_spike and convergence — those come from analysis results
    if (rule.signalType === 'temporal_spike' || rule.signalType === 'convergence') continue;

    if (rule.eventTypes.has(event.type)) {
      types.push(rule.signalType);
      continue;
    }

    if (rule.keywords.some(kw => text.includes(kw))) {
      types.push(rule.signalType);
    }
  }

  return types;
}

/** Determine max severity from a list of events. */
function maxSeverity(events: NormalizedEvent[]): Severity {
  const levels: Record<Severity, number> = { critical: 4, high: 3, medium: 2, low: 1, info: 0 };
  let max: Severity = 'info';

  for (const e of events) {
    const sev = e.severity ?? 'info';
    if (levels[sev] > levels[max]) {
      max = sev;
    }
  }

  return max;
}

// ---------------------------------------------------------------------------
// Main function
// ---------------------------------------------------------------------------

/**
 * Aggregate intelligence signals across events, producing country-level
 * summaries with convergence scoring.
 *
 * @param events - All current events
 * @param analysisResult - Results from other analysis modules
 * @returns Array of country-level aggregated signals
 */
export function aggregateSignals(
  events: NormalizedEvent[],
  analysisResult: AnalysisResult,
): AggregatedSignal[] {
  const cutoff = Date.now() - SIX_HOURS_MS;

  // Group events by country
  const countryEvents = new Map<string, { code: string; name: string; events: NormalizedEvent[] }>();

  for (const event of events) {
    if (getEventTimestamp(event) < cutoff) continue;

    const code = event.location?.countryCode?.toUpperCase();
    const name = event.location?.country;

    if (!code || !name) continue;

    if (!countryEvents.has(code)) {
      countryEvents.set(code, { code, name, events: [] });
    }
    countryEvents.get(code)!.events.push(event);
  }

  const results: AggregatedSignal[] = [];

  for (const [code, data] of countryEvents) {
    // Aggregate signal types for this country
    const signalCounts = new Map<string, { count: number; events: NormalizedEvent[] }>();

    for (const event of data.events) {
      const signalTypes = classifySignal(event);
      for (const st of signalTypes) {
        if (!signalCounts.has(st)) {
          signalCounts.set(st, { count: 0, events: [] });
        }
        const entry = signalCounts.get(st)!;
        entry.count++;
        entry.events.push(event);
      }
    }

    // Add temporal spike signals from surge detection
    for (const surge of analysisResult.surges) {
      // Check if this surge's events overlap with this country
      // (surges are by type, not country, so this is approximate)
      if (surge.region !== 'Global') {
        // We can't map directly, but include if country has events of this type
        const hasType = data.events.some(e => e.type === surge.sourceType);
        if (hasType) {
          if (!signalCounts.has('temporal_spike')) {
            signalCounts.set('temporal_spike', { count: 0, events: [] });
          }
          signalCounts.get('temporal_spike')!.count++;
        }
      }
    }

    // Add convergence signals from convergence detection
    let convergenceZone: string | null = null;
    for (const conv of analysisResult.convergences) {
      // Check if any convergence cell is in this country
      const relevantEvents = data.events.filter(e =>
        e.location?.lat != null && e.location?.lng != null &&
        Math.abs(e.location.lat - conv.lat) < 1 &&
        Math.abs(e.location.lng - conv.lng) < 1
      );
      if (relevantEvents.length > 0) {
        if (!signalCounts.has('convergence')) {
          signalCounts.set('convergence', { count: 0, events: [] });
        }
        signalCounts.get('convergence')!.count++;
        convergenceZone = conv.label;
      }
    }

    // Only report countries with 1+ signal types
    if (signalCounts.size < 1) continue;

    // Build signal array
    const signals: AggregatedSignal['signals'] = [];
    for (const [type, entry] of signalCounts) {
      signals.push({
        type,
        count: entry.count,
        severity: entry.events.length > 0 ? maxSeverity(entry.events) : 'info',
      });
    }
    signals.sort((a, b) => b.count - a.count);

    // Convergence score: signal diversity × event density
    const convergenceScore = Math.min(100, signals.length * 15 + Math.min(data.events.length * 2, 40));

    results.push({
      country: data.name,
      countryCode: code,
      signals,
      convergenceScore,
      zone: convergenceZone,
    });
  }

  // Sort by convergence score descending
  results.sort((a, b) => b.convergenceScore - a.convergenceScore);

  return results;
}
